/* eslint-disable @nx/enforce-module-boundaries */
import { SearchLineIcon, RemoveUserIcon } from '@mybridge/icons';
import {
  Avatar,
  Button,
  Checkbox,
  Divider,
  HStack,
  Heading,
  Input,
  InputGroup,
  List,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  Spinner,
} from '@mybridge/ui';
import { forwardRef, useContext, useEffect, useState } from 'react';
import { getUserFullName, getUserProfilePic } from 'v4/lib/commons';
import { TeamsContext } from '../context';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';
import { useDirection } from '@mybridge/ui/custom-hooks/useDirection';
import { NoMembersFoundIll } from '../manage/panels/404';
import { useDispatch } from 'react-redux';
import { useInfiniteQuery } from '@tanstack/react-query';
import { flattenQueryResults, getNextPageFromURL } from 'v4/utils/utils';
import { getDegreeConnectionsSelfUser } from 'v4/store/actions/connection.actions';
import { isMobile } from 'react-device-detect';

export const TeamInviteConnectionsModal = forwardRef(({ ...props }, ref) => {
  const dispatch = useDispatch();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search || '');
    }, 300);
    return () => clearTimeout(handler);
  }, [search]);

  const {
    invitationDisc,
    refetchInvitedUsers,
    teamInvitedUsers,
    teamMembers,
    inviteUsersToTeam,
    inviteUserToTeamLoading
  } = useContext(TeamsContext);
  const { t } = useTranslator();
  const direction = useDirection();
  const [selected, setSelected] = useState([]);

  const {
    data: connectionsData,
    fetchNextPage,
    isFetching,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['invite-connections-teams', debouncedSearch],
    queryFn: async ({ pageParam }) => {
      const resp = await dispatch(
        getDegreeConnectionsSelfUser({
          degree: 1,
          query: debouncedSearch,
          pageParam,
        })
      );
      return resp?.payload ?? {};
    },
    getNextPageParam: (lastPage) => getNextPageFromURL(lastPage?.next),
    refetchOnWindowFocus: false,
  });

  const connections = flattenQueryResults(connectionsData) || [];

  const filteredConnections = connections
    ?.filter?.((c) =>
      search?.length
        ? getUserFullName(c)
            ?.toLowerCase?.()
            ?.includes?.(search?.toLowerCase?.())
        : true
    )
    ?.filter?.(
      (c) => !teamInvitedUsers?.find((iu) => iu?.user?.id === c?.id)
    )
    ?.filter?.((c) => !teamMembers?.find((iu) => iu?.user?.id === c?.id));
    
  useEffect(() => {
    refetchInvitedUsers?.();
  }, [refetchInvitedUsers]);
  const toggleSelected = (item) => {
    const itemId = item?.id;
    const isSelected = selected.some(
      (selectedItem) => selectedItem.id === itemId
    );
    if (isSelected) {
      const updatedSelected = selected.filter(
        (selectedItem) => selectedItem.id !== itemId
      );
      setSelected(updatedSelected);
    } else {
      setSelected([...selected, item]);
    }
  };
  const handleInvite = async () => {
    const data = await inviteUsersToTeam?.(selected);
    props?.handleResponce &&  props.handleResponce(data);
    invitationDisc?.onClose?.()
  };

  return (
    <>
      <Modal size="2xl" {...(invitationDisc ?? {})}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Text color="#3D5A80" fontSize="22px" lineHeight="26px">
              {t('teams.inviteConnections') || "Invite connections"}
            </Text>
            <ModalCloseButton {...(direction === 'rtl') ? {left: '0.75rem', right: 'auto'} : {left: 'auto', right: '0.75rem'}} />
          </ModalHeader>
          <ModalBody>
            <Stack>
              <HStack>
                <InputGroup>
                  <Stack position="absolute" zIndex="1" {...(direction === 'rtl') ? {mr: "10px"} : {ml: "10px"}} mt="15px">
                    <SearchLineIcon />
                  </Stack>

                  <Input
                    // pl="40px"
                    {...(direction === 'rtl') ? {pr: "40px"} : {pl: "40px"}}
                    onChange={(e) => setSearch(e?.target?.value)}
                    autoFocus
                    placeholder={t('teams.searchByName') || "Search by name"}
                    borderRadius="30px"
                  />
                </InputGroup>
              </HStack>
              <Divider />
              <HStack justifyContent={filteredConnections?.length ? "flex-start" : "center"} alignItems="flex-start" w="100%">
                {filteredConnections?.length ? (
                  <>
                    <Stack maxW="408px" w="55%">
                      <List
                        className="member-list"
                        as={Stack}
                        spacing={0}
                        maxH="64"
                        overflowY={isMobile ? "auto" : "hidden"}
                        _hover={!isMobile ? { overflowY: 'auto' } : {}}
                        onScroll={(e) => {
                          const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
                          if (scrollHeight - scrollTop - clientHeight < 50) {
                            if (hasNextPage && !isFetchingNextPage) {
                              fetchNextPage();
                            }
                          }
                        }}
                      >
                        {filteredConnections?.map?.((c, cIndex) => (
                          <ListItem key={cIndex}>
                            <HStack w="100%" alignItems="flex-start">
                              <Checkbox
                                isChecked={selected.some(
                                  (selectedItem) => selectedItem.id === c?.id
                                )}
                                onChange={() => toggleSelected(c)}
                                borderRadius={5}
                                p={2}
                                flex={1}
                                _hover={{ bg: 'gray.100' }}
                              >
                                <HStack alignItems="flex-start">
                                  <Avatar
                                    size={['sm', 'md']}
                                    name={getUserFullName(c)}
                                    src={getUserProfilePic(c)}
                                  />
                                  <Stack spacing={0}>
                                    <Text fontSize={["sm", "md"]}>{getUserFullName(c)}</Text>
                                    <Text fontSize={["xs","sm"]} color="blackAlpha.500">
                                      {c?.position ??
                                        c?.experiences?.[0]?.name ??
                                        c?.educations?.[0]?.name}
                                    </Text>
                                  </Stack>
                                </HStack>
                              </Checkbox>
                            </HStack>
                          </ListItem>
                        ))}
                        {isFetchingNextPage && (
                          <ListItem display="flex" justifyContent="center" py={2}>
                            <Spinner size="sm" />
                          </ListItem>
                        )}
                      </List>
                    </Stack>
                    {selected?.length > 0 && (
                      <Divider orientation="vertical" h="247px" />
                    )}
                    {selected?.length > 0 && (
                      <Stack maxW="247px" w="45%" maxH="64" overflowY="auto" px={2}>
                        <List>
                          <HStack justifyContent="space-between" mb={4}>
                            <Stack cursor="pointer">
                              <Text
                                fontSize={["11px","15px"]}
                                color="#868585"
                                lineHeight="18px"
                              >
                                {selected?.length} {t('teams.selected') || "Selected"}
                              </Text>
                            </Stack>
                            <Stack cursor="pointer">
                              <Text
                                fontSize={["11px","15px"]}
                                color="#868585"
                                lineHeight="18px"
                                onClick={() => setSelected([])}
                              >
                                {t('teams.unselectAll') || "Unselect all"}
                              </Text>
                            </Stack>
                          </HStack>
                          {selected?.map?.((c, cIndex) => (
                            <ListItem key={cIndex}>
                              <HStack w="100%" alignItems="flex-start" mb={3}>
                                <HStack justifyContent="center" alignItems="center" flex="1">
                                  <Avatar
                                    size={['sm', 'md']}
                                    name={getUserFullName(c)}
                                    src={getUserProfilePic(c)}
                                  />
                                  <Stack spacing={0} flex="1">
                                    <Text fontSize={["sm", "md"]}>{getUserFullName(c)}</Text>
                                  </Stack>
                                  <Stack
                                    cursor="pointer"
                                    onClick={() => toggleSelected(c)}
                                  >
                                    <RemoveUserIcon />
                                  </Stack>
                                </HStack>
                              </HStack>
                            </ListItem>
                          ))}
                        </List>
                      </Stack>
                    )}
                  </>
                ) : isFetching ? (
                  <HStack justifyContent="center" py={4} w="100%">
                    <Spinner />
                  </HStack>
                ) : (
                  <HStack justifyContent="center" py={4} w="100%">
                    <Stack maxW="96" alignItems="center">
                      <NoMembersFoundIll w="72" />
                      <Heading size="sm">
                        {t('postEditor.noMentionsFound') || "No users found"}
                      </Heading>
                    </Stack>
                  </HStack>
                )}
              </HStack>
            </Stack>
          </ModalBody>
          <ModalFooter>
            <HStack justifyContent="flex-end">
              <Button
                borderRadius="50px"
                onClick={(e) => invitationDisc?.onClose?.()}
                variant="secondary"
              >
                {t('buttons.cancel') || "Cancel"}
              </Button>
              <Button
                borderRadius="50px"
                variant="primary"
                isLoading={inviteUserToTeamLoading}
                onClick={() => selected?.length > 0 ? handleInvite() : null}
                isDisabled={selected?.length === 0 || inviteUserToTeamLoading}
              >
                {t('eventDashboard.invite') || "Invite"} {selected?.length > 0 ? selected?.length : ''}
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
});
